import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Heart, MessageCircle, Trash2, Edit, Send, Reply, X, Flag, Lock } from "lucide-react";
import { ReportContentDialog } from "@/components/community/ReportContentDialog";
import { ReactionButton } from "@/components/community/ReactionButton";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { checkRateLimit } from "@/lib/rateLimiting";
import { commentSchema } from "@/lib/validationSchemas";
import { sanitizeText } from "@/lib/sanitize";

interface Post {
  id: string;
  title: string;
  content: string;
  category: string;
  is_anonymous: boolean;
  created_at: string;
  user_id: string | null;
  is_locked: boolean;
  locked_at: string | null;
  locked_by: string | null;
  lock_reason: string | null;
  author_display_name?: string | null;
  author_avatar_url?: string | null;
  user_roles?: string[];
}

interface Comment {
  id: string;
  content: string;
  is_anonymous: boolean;
  created_at: string;
  user_id: string | null;
  parent_comment_id: string | null;
  author_display_name?: string | null;
  author_avatar_url?: string | null;
  user_roles?: string[];
  replies?: Comment[];
}

export default function CommunityPost() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<{ id: string; name: string } | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ postId?: string; commentId?: string; type: "post" | "comment" }>({ type: "post" });
  const [myContentIds, setMyContentIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchPost();
    fetchComments();
    getCurrentUser();
    fetchMyContentIds();


    // Subscribe to realtime comment updates
    const channel = supabase
      .channel(`post-${id}-comments`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'community_comments',
        filter: `post_id=eq.${id}`
      }, () => {
        fetchComments();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchPost = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('community_posts')
        .select('id, title, content, category, is_anonymous, created_at, visible_author_id, is_locked, locked_at, locked_by, lock_reason')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast({
          title: "Post not found",
          description: "This post may have been removed",
          variant: "destructive"
        });
        navigate('/community');
        return;
      }

      const authorId = (data as any).visible_author_id as string | null;

      // Only non-anonymous authors expose an identity
      const { data: profileRow } = authorId
        ? await supabase
            .from('public_profiles')
            .select('display_name, avatar_url')
            .eq('user_id', authorId)
            .maybeSingle()
        : { data: null as any };

      const { data: roles } = authorId
        ? await supabase.from('user_roles').select('role').eq('user_id', authorId)
        : { data: [] as any[] };
      
      setPost({
        ...(data as any),
        user_id: authorId,
        author_display_name: profileRow?.display_name ?? null,
        author_avatar_url: profileRow?.avatar_url ?? null,
        user_roles: (roles || []).map((r: any) => r.role)
      } as Post);
    } catch (error: any) {
      toast({
        title: "Error loading post",
        description: error.message,
        variant: "destructive"
      });
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyContentIds = async () => {
    const { data } = await supabase
      .from('community_authorship')
      .select('content_id');
    setMyContentIds(new Set((data || []).map((r: any) => r.content_id)));
  };

  const fetchComments = async () => {
    if (!id) return;

    try {
      const { data, error} = await supabase
        .from('community_comments')
        .select('id, post_id, content, is_anonymous, created_at, parent_comment_id, visible_author_id')
        .eq('post_id', id)
        .eq('moderation_status', 'approved')
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch profiles/roles for all non-anonymous comment authors
      const userIds = Array.from(new Set(
        (data || []).map((c: any) => c.visible_author_id).filter(Boolean)
      )) as string[];

      const { data: profileRows } = userIds.length
        ? await supabase
            .from('public_profiles')
            .select('user_id, display_name, avatar_url')
            .in('user_id', userIds)
        : { data: [] as any[] };

      const { data: roles } = userIds.length
        ? await supabase.from('user_roles').select('user_id, role').in('user_id', userIds)
        : { data: [] as any[] };

      const profileMap = (profileRows || []).reduce((acc, p: any) => {
        acc[p.user_id] = p;
        return acc;
      }, {} as Record<string, any>);

      // Build roles map
      const rolesMap = (roles || []).reduce((acc, role: any) => {
        if (!acc[role.user_id]) acc[role.user_id] = [];
        acc[role.user_id].push(role.role);
        return acc;
      }, {} as Record<string, string[]>);
      
      // Organize comments into a nested structure
      const commentsMap = new Map<string, Comment>();
      const topLevelComments: Comment[] = [];
      
      (data || []).forEach((comment: any) => {
        const authorId = comment.visible_author_id as string | null;
        commentsMap.set(comment.id, { 
          ...comment,
          user_id: authorId,
          author_display_name: authorId ? (profileMap[authorId]?.display_name ?? null) : null,
          author_avatar_url: authorId ? (profileMap[authorId]?.avatar_url ?? null) : null,

          user_roles: authorId ? rolesMap[authorId] || [] : [],
          replies: [] 
        } as Comment);
      });
      
      (data || []).forEach((comment: any) => {
        if (comment.parent_comment_id) {
          const parent = commentsMap.get(comment.parent_comment_id);
          if (parent) {
            parent.replies!.push(commentsMap.get(comment.id)!);
          }
        } else {
          topLevelComments.push(commentsMap.get(comment.id)!);
        }
      });
      
      setComments(topLevelComments);
    } catch (error: any) {
      console.error('Error loading comments:', error);
    }
  };


  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    // Check if post is locked
    if (post?.is_locked) {
      toast({
        title: "Post is locked",
        description: "This discussion has been locked and no longer accepts new comments.",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to comment",
          variant: "destructive"
        });
        return;
      }

      // Validate comment content
      const validation = commentSchema.safeParse({ content: newComment });
      if (!validation.success) {
        toast({
          title: "Invalid comment",
          description: validation.error.errors[0].message,
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Check rate limit
      const rateLimitResult = await checkRateLimit(user.id, 'community_comment');
      if (!rateLimitResult.allowed) {
        toast({
          title: "Too many comments",
          description: rateLimitResult.error,
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('community_comments')
        .insert({
          post_id: id,
          user_id: user.id,
          content: newComment.trim(),
          is_anonymous: isAnonymous,
          parent_comment_id: replyingTo?.id || null
        });

      if (error) throw error;

      setNewComment("");
      setIsAnonymous(false);
      setReplyingTo(null);
      
      toast({
        title: replyingTo ? "Reply posted!" : "Comment posted!",
        description: replyingTo ? "Your reply has been added" : "Your comment has been added"
      });
    } catch (error: any) {
      toast({
        title: "Error posting comment",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!post || !confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('community_posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;

      toast({
        title: "Post deleted",
        description: "Your post has been removed"
      });

      navigate('/community');
    } catch (error: any) {
      toast({
        title: "Error deleting post",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;

    try {
      const { error } = await supabase
        .from('community_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      toast({
        title: "Comment deleted",
        description: "Your comment has been removed"
      });
    } catch (error: any) {
      toast({
        title: "Error deleting comment",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <p className="text-muted-foreground">Loading post...</p>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const countAllComments = (comments: Comment[]): number => {
    return comments.reduce((total, comment) => {
      return total + 1 + (comment.replies ? countAllComments(comment.replies) : 0);
    }, 0);
  };

  const renderComment = (comment: Comment, depth: number = 0) => (
    <div key={comment.id} className={depth > 0 ? "ml-8 mt-4" : ""}>
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Avatar className="h-9 w-9 mt-1">
              <AvatarImage src={comment.author_avatar_url || undefined} />
              <AvatarFallback className="text-xs bg-secondary/50">
                {comment.is_anonymous ? "?" : (comment.author_display_name?.charAt(0).toUpperCase() || "?")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {comment.is_anonymous ? "Anonymous" : (comment.author_display_name || "Community Member")}
                    </span>
                    {!comment.is_anonymous && comment.user_roles && comment.user_roles.length > 0 && (
                      <>
                        {comment.user_roles.includes('admin') && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Admin</Badge>
                        )}
                        {comment.user_roles.includes('moderator') && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Moderator</Badge>
                        )}
                      </>
                    )}
                    <span>•</span>
                    <span>{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}</span>
                  </div>
                <div className="flex items-center gap-1">
                  {myContentIds.has(comment.id) ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReportTarget({ commentId: comment.id, type: "comment" });
                        setReportDialogOpen(true);
                      }}
                      className="h-8 gap-1 text-xs"
                    >
                      <Flag className="h-3 w-3" />
                      Report
                    </Button>
                  )}
                </div>
              </div>
              <p 
                className="text-foreground whitespace-pre-wrap mb-3"
                dangerouslySetInnerHTML={{ __html: sanitizeText(comment.content) }}
              />
              <div className="flex items-center gap-2">
                <ReactionButton commentId={comment.id} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (post.is_locked) {
                      toast({
                        title: "Post is locked",
                        description: "This discussion is locked and no longer accepts replies.",
                        variant: "destructive"
                      });
                      return;
                    }
                    setReplyingTo({
                      id: comment.id,
                      name: comment.is_anonymous ? "Anonymous" : (comment.author_display_name || "Community Member")
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="gap-1"
                  disabled={post.is_locked}
                >
                  <Reply className="h-3 w-3" />
                  Reply
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-4 mt-4">
          {comment.replies.map((reply) => renderComment(reply, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/community">
          <Button variant="ghost" className="mb-6 gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Community
          </Button>
        </Link>

        {/* Post Card */}
        <Card className="mb-6">
          <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-4 flex-1">
              <Avatar className="h-12 w-12">
                <AvatarImage src={post.author_avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10">
                  {post.is_anonymous ? "?" : (post.author_display_name?.charAt(0).toUpperCase() || "?")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Badge>{post.category.replace('_', ' ')}</Badge>
                    {post.is_locked && (
                    <Badge variant="secondary" className="bg-orange-100 dark:bg-orange-900 text-orange-900 dark:text-orange-100 gap-1">
                      <Lock className="h-3 w-3" /> Locked
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {post.is_anonymous ? "Anonymous" : (post.author_display_name || "Community Member")}
                  </span>
                  {!post.is_anonymous && post.user_roles && post.user_roles.length > 0 && (
                    <>
                      {post.user_roles.includes('admin') && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Admin</Badge>
                      )}
                      {post.user_roles.includes('moderator') && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Moderator</Badge>
                      )}
                    </>
                  )}
                  <span className="text-sm text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                  </span>
                </div>
                <h1 className="text-3xl font-bold mb-4">{post.title}</h1>
                <p 
                  className="text-foreground whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: sanitizeText(post.content) }}
                />
              </div>
            </div>
              {myContentIds.has(post.id) ? (
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={handleDeletePost}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReportTarget({ postId: post.id, type: "post" });
                    setReportDialogOpen(true);
                  }}
                  className="gap-2"
                >
                  <Flag className="h-4 w-4" />
                  Report
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MessageCircle className="h-4 w-4" />
                <span>{comments.length}</span>
              </div>
              <ReactionButton postId={post.id} />
            </div>
          </CardContent>
        </Card>

        {/* Comments Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Comments ({countAllComments(comments)})</h2>

          {/* Add Comment Form */}
          <Card>
            <CardContent className="pt-6">
              {post.is_locked ? (
                <div className="p-6 bg-secondary/30 rounded-lg text-center">
                  <div className="mb-3 flex justify-center"><Lock className="h-6 w-6 text-muted-foreground" /></div>
                  <h3 className="font-semibold mb-2">This discussion is locked</h3>
                  <p className="text-sm text-muted-foreground">
                    {post.lock_reason || "This post has been locked after 12 months of inactivity. While you can't add new comments, you can still read the discussion and learn from it."}
                  </p>
                  {post.locked_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Locked {formatDistanceToNow(new Date(post.locked_at), { addSuffix: true })}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {replyingTo && (
                    <div className="mb-4 p-3 bg-secondary/50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <Reply className="h-4 w-4" />
                        <span>Replying to <span className="font-medium">{replyingTo.name}</span></span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyingTo(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <form onSubmit={handleSubmitComment} className="space-y-4">
                    <Textarea
                      placeholder={replyingTo ? "Write your reply..." : "Share your thoughts or support..."}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                      maxLength={2000}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="comment-anonymous"
                          checked={isAnonymous}
                          onCheckedChange={setIsAnonymous}
                        />
                        <Label htmlFor="comment-anonymous" className="text-sm">
                          Post anonymously
                        </Label>
                      </div>
                      <Button type="submit" disabled={submitting || !newComment.trim()} className="gap-2">
                        <Send className="h-4 w-4" />
                        {submitting ? "Posting..." : (replyingTo ? "Reply" : "Post Comment")}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </CardContent>
          </Card>

          {/* Comments List */}
          {comments.length === 0 ? (
            <Card className="p-8 text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">No comments yet. Be the first to share your thoughts!</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => renderComment(comment))}
            </div>
          )}
        </div>
      </div>

      <ReportContentDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        postId={reportTarget.postId}
        commentId={reportTarget.commentId}
        contentType={reportTarget.type}
      />
    </div>
  );
}